import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { HieraCallout, HieraGif, HieraMermaid, HieraVideo } from "./nodes";
import { SlashMenu, type SlashItem } from "./SlashMenu";
import { hieraApi } from "../lib/api";

type Props = {
  pageId: string;
  spaceId: string;
  initialBody: unknown;
  initialTitle: string;
  updatedAt: string;
  editable: boolean;
  toolbar?: boolean;
  belowTitle?: ReactNode;
  onTitleChange: (t: string) => void;
  onSaved: (meta: { updatedAt: string; body?: unknown; flash?: string }) => void;
  langSavedLabel: string;
  langAutosavedLabel: string;
};

export function PageEditor({
  pageId,
  spaceId,
  initialBody,
  initialTitle,
  updatedAt,
  editable,
  toolbar,
  belowTitle,
  onTitleChange,
  onSaved,
  langSavedLabel,
  langAutosavedLabel,
}: Props) {
  const [slash, setSlash] = useState<{ items: SlashItem[]; top: number; left: number } | null>(null);
  const updatedAtRef = useRef(updatedAt);
  const saveTimer = useRef<number | null>(null);
  const draftTimer = useRef<number | null>(null);
  const ready = useRef(false);
  const saveChain = useRef(Promise.resolve());

  useEffect(() => {
    updatedAtRef.current = updatedAt;
  }, [updatedAt]);

  const editor = useEditor({
    editable,
    extensions: [
      StarterKit.configure({ codeBlock: { HTMLAttributes: { class: "code-block" } } }),
      Image.configure({ allowBase64: false, HTMLAttributes: { class: "hiera-img" } }),
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: "Type / for commands" }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      HieraVideo,
      HieraGif,
      HieraCallout,
      HieraMermaid,
    ],
    content: (initialBody as object) || { type: "doc", content: [{ type: "paragraph" }] },
    onCreate: () => {
      window.setTimeout(() => {
        ready.current = true;
      }, 400);
    },
    onUpdate: ({ editor: ed }) => {
      if (!editable || !ready.current) return;
      if (draftTimer.current) window.clearTimeout(draftTimer.current);
      draftTimer.current = window.setTimeout(() => {
        void hieraApi.saveDraft(pageId, { body: ed.getJSON(), title: initialTitle });
      }, 400);
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => {
        void persist(ed.getJSON(), initialTitle, false);
      }, 800);
    },
  });

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editable, editor]);

  function persist(body: unknown, title: string, manual: boolean) {
    const job = saveChain.current.then(() => persistNow(body, title, manual));
    saveChain.current = job.then(
      () => undefined,
      () => undefined,
    );
    return job;
  }

  async function persistNow(body: unknown, title: string, manual: boolean) {
    try {
      const res = await hieraApi.patchPage(pageId, {
        body,
        title,
        updatedAt: updatedAtRef.current,
      });
      updatedAtRef.current = res.page.updated_at;
      onSaved({
        updatedAt: res.page.updated_at,
        body: res.page.body ?? body,
        flash: manual ? langSavedLabel : langAutosavedLabel,
      });
    } catch (err: unknown) {
      const fresh = (err as { status?: number; body?: { page?: { updated_at?: string } } }).body?.page?.updated_at;
      const status = (err as { status?: number }).status;
      if (status !== 409 || !fresh || fresh === updatedAtRef.current) return;
      updatedAtRef.current = fresh;
      try {
        const res = await hieraApi.patchPage(pageId, { body, title, updatedAt: fresh });
        updatedAtRef.current = res.page.updated_at;
        onSaved({ updatedAt: res.page.updated_at, body: res.page.body ?? body });
      } catch {
        /* следующий ввод повторит сохранение */
      }
    }
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (editor) void persist(editor.getJSON(), initialTitle, true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editor, initialTitle]);

  useEffect(() => {
    if (!editor) return;
    const onSlash = () => {
      const { from } = editor.state.selection;
      const text = editor.state.doc.textBetween(Math.max(0, from - 20), from, "\n");
      if (!text.endsWith("/")) {
        setSlash(null);
        return;
      }
      const coords = editor.view.coordsAtPos(from);
      setSlash({
        top: coords.bottom + 8,
        left: coords.left,
        items: slashItems(async (item) => {
          setSlash(null);
          editor.chain().focus().deleteRange({ from: from - 1, to: from }).run();
          await runSlash(item, editor, pageId, spaceId);
        }),
      });
    };
    editor.on("selectionUpdate", onSlash);
    editor.on("update", onSlash);
    return () => {
      editor.off("selectionUpdate", onSlash);
      editor.off("update", onSlash);
    };
  }, [editor, pageId, spaceId]);

  return (
    <div className="editor-wrap">
      {toolbar && editor ? <EditorTools editor={editor} /> : null}
      <input
        className="page-title-input"
        value={initialTitle}
        disabled={!editable}
        onChange={(e) => onTitleChange(e.target.value)}
        onBlur={() => {
          if (!editable || !pageId || !editor) return;
          void persist(editor.getJSON(), initialTitle, false);
        }}
      />
      {belowTitle}
      <EditorContent editor={editor} className="tiptap" />
      {slash && <SlashMenu {...slash} onClose={() => setSlash(null)} />}
    </div>
  );
}

function EditorTools({ editor }: { editor: NonNullable<ReturnType<typeof useEditor>> }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((n) => n + 1);
    editor.on("transaction", bump);
    return () => {
      editor.off("transaction", bump);
    };
  }, [editor]);

  function act(run: () => void) {
    return (e: MouseEvent) => {
      e.preventDefault();
      run();
    };
  }

  const items = [
    { id: "b", label: "B", on: editor.isActive("bold"), run: () => editor.chain().focus().toggleBold().run() },
    { id: "i", label: "I", on: editor.isActive("italic"), run: () => editor.chain().focus().toggleItalic().run() },
    { id: "s", label: "S", on: editor.isActive("strike"), run: () => editor.chain().focus().toggleStrike().run() },
    { id: "code", label: "</>", on: editor.isActive("code"), run: () => editor.chain().focus().toggleCode().run() },
    { id: "h1", label: "H1", on: editor.isActive("heading", { level: 1 }), run: () => editor.chain().focus().toggleHeading({ level: 1 }).run() },
    { id: "h2", label: "H2", on: editor.isActive("heading", { level: 2 }), run: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
    { id: "h3", label: "H3", on: editor.isActive("heading", { level: 3 }), run: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
    { id: "ul", label: "•", on: editor.isActive("bulletList"), run: () => editor.chain().focus().toggleBulletList().run() },
    { id: "ol", label: "1.", on: editor.isActive("orderedList"), run: () => editor.chain().focus().toggleOrderedList().run() },
    { id: "q", label: "❝", on: editor.isActive("blockquote"), run: () => editor.chain().focus().toggleBlockquote().run() },
    {
      id: "a",
      label: "↗",
      on: editor.isActive("link"),
      run: () => {
        const prev = String(editor.getAttributes("link").href || "");
        const href = window.prompt("https://", prev || "https://");
        if (href === null) return;
        if (!href.trim()) {
          editor.chain().focus().unsetLink().run();
          return;
        }
        editor.chain().focus().setLink({ href: href.trim() }).run();
      },
    },
  ];

  return (
    <div className="editor-tools">
      {items.map((item) => (
        <button key={item.id} type="button" className={item.on ? "is-on" : ""} onMouseDown={act(item.run)}>
          {item.label}
        </button>
      ))}
    </div>
  );
}

function slashItems(run: (id: string) => void | Promise<void>): SlashItem[] {
  const mk = (id: string, label: string, hint: string): SlashItem => ({
    id,
    label,
    hint,
    run: () => void run(id),
  });
  return [
    mk("text", "Text", "Paragraph"),
    mk("todo", "To-do list", "Tasks"),
    mk("h1", "Heading 1", ""),
    mk("h2", "Heading 2", ""),
    mk("h3", "Heading 3", ""),
    mk("h4", "Heading 4", ""),
    mk("bullet", "Bullet list", ""),
    mk("ordered", "Numbered list", ""),
    mk("quote", "Quote", ""),
    mk("code", "Code", ""),
    mk("divider", "Divider", ""),
    mk("image", "Image", "AVIF via Lustra"),
    mk("gif", "GIF", "Autoplay"),
    mk("video", "Video", "Play controls"),
    mk("callout", "Callout", ""),
    mk("table", "Table", ""),
    mk("mermaid", "Mermaid", "Diagram"),
    mk("subpage", "Sub-page", "Draft child"),
  ];
}

async function runSlash(
  id: string,
  editor: NonNullable<ReturnType<typeof useEditor>>,
  pageId: string,
  spaceId: string,
) {
  const chain = editor.chain().focus();
  switch (id) {
    case "h1":
      chain.toggleHeading({ level: 1 }).run();
      break;
    case "h2":
      chain.toggleHeading({ level: 2 }).run();
      break;
    case "h3":
      chain.toggleHeading({ level: 3 }).run();
      break;
    case "h4":
      chain.toggleHeading({ level: 4 }).run();
      break;
    case "bullet":
      chain.toggleBulletList().run();
      break;
    case "ordered":
      chain.toggleOrderedList().run();
      break;
    case "todo":
      chain.toggleTaskList().run();
      break;
    case "quote":
      chain.toggleBlockquote().run();
      break;
    case "code":
      chain.toggleCodeBlock().run();
      break;
    case "divider":
      chain.setHorizontalRule().run();
      break;
    case "table":
      chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
      break;
    case "callout":
      chain.insertContent({ type: "hieraCallout", content: [{ type: "paragraph" }] }).run();
      break;
    case "mermaid":
      chain.insertContent({ type: "hieraMermaid", attrs: { code: "flowchart LR\n  A-->B" } }).run();
      break;
    case "image":
    case "gif":
    case "video": {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = id === "video" ? "video/mp4,video/webm" : "image/*";
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        const { asset } = await hieraApi.upload(file, { spaceId, pageId });
        if (asset.kind === "video") {
          editor.chain().focus().insertContent({ type: "hieraVideo", attrs: { src: asset.public_url } }).run();
        } else if (asset.kind === "gif") {
          editor.chain().focus().insertContent({ type: "hieraGif", attrs: { src: asset.public_url } }).run();
        } else {
          editor.chain().focus().setImage({ src: asset.public_url }).run();
        }
      };
      input.click();
      break;
    }
    case "subpage": {
      const created = await hieraApi.createPage(spaceId, {
        title: "Untitled",
        parentId: pageId,
      });
      chain.insertContent(`[[${String(created.page.title || "Untitled")}]]`).run();
      break;
    }
    default:
      chain.setParagraph().run();
  }
}
